// Diagnostic suite for cross-device pinned-thread sync.
//
// Mirrors kaether_for_ios/Stoat/E2EE/PinnedThreadSelfTest.swift.
//
// Sections:
//
//   A. Codec — pure (de)serialisation, prefix non-collision against sibling
//      control envelopes, and rejection of malformed / wrong-schema /
//      empty-channelId payloads.
//
//   B. Store mechanics — single per-user pinned set, idempotent re-pins,
//      monotonic ts rules that drop out-of-order events, persistence
//      across instances, resetAll.
//
//   C. Cap enforcement — pinning the 9th thread evicts the oldest by
//      `pinnedTs` (FIFO), the evicted record is no longer reported by
//      `pinnedRecord(...)`, and the cap matches MAX_PINNED_THREADS.
//
//   D. Sorting + listing — `pinnedChannelIds()` is sorted by `pinnedTs`
//      descending; ties are broken stably by channelId ascending.
//
//   E. Server-rebind resistance + hardening — a forged channelId stays
//      scoped to that channelId; a stale unpin (ts < recorded pin ts) is
//      dropped; the codec rejects an unpin with empty channelId.
//
// All cases use isolated per-test DB names so the suite is safe to run
// concurrently and idempotently.

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import { PinnedThreadCodec, PinnedThreadCodecError } from "./PinnedThread";
import type { PinnedThreadPayload } from "./PinnedThread";
import { PinnedThreadStore, MAX_PINNED_THREADS } from "./PinnedThreadStore";

SelfTestRunner.register({
  name: "Pinned threads (cross-device sync)",
  isVerified: true,
  run: async () =>
    buildSuiteResult(await Promise.all([
      // A. Codec
      testCodecRoundTripPin(),
      testCodecRoundTripUnpin(),
      testIsPinEventDistinguishesSiblingPrefixes(),
      testTryDecodeReturnsNilForUserMessage(),
      testEmptyChannelIdRejected(),
      testUnsupportedSchemaRejected(),
      // B. Store mechanics
      testPinChannelPresentInStore(),
      testUnpinChannelRemoved(),
      testRePinIdempotent(),
      testOutOfOrderUnpinRejected(),
      testOutOfOrderPinReplayDoesNotBumpObservedAt(),
      testPersistenceAcrossInstances(),
      testResetAllEmptiesStore(),
      // C. Cap enforcement
      testCap8AllPresent(),
      testNinthPinEvictsOldest(),
      testNinthPinEvictsDeterministic(),
      testEvictedRecordAbsent(),
      // D. Sorting
      testPinnedChannelIdsSortedDesc(),
      testTiesBrokenByChannelIdAsc(),
      // E. Server-rebind resistance + hardening
      testLastEventTsGateStaleUnpinIgnored(),
      testLastEventTsGateFreshUnpinApplied(),
      testForgedChannelIdScopedToThatChannel(),
      testCodecRejectsUnpinWithEmptyChannelId(),
    ])),
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Suite-wide unique tag so DB names do not collide across dev launches.
 *  IndexedDB persists across page reloads, so a counter alone reused stale
 *  state from a prior run (e.g. a previous launch left
 *  `lastEventTs[chA] = 6`, which then silently swallowed `pin@ts=5` in
 *  the same-numbered case on the next launch — producing the
 *  "changed=false isPinned=false" divergence). Tagging the DB name with
 *  a per-launch UUID makes every dev launch see a virgin DB.
 *  Mirrors the fix in `profile-share/ProfileSharePromptSelfTest.ts`. */
const dbSuiteTag =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Unique DB name per call so each case runs in isolation. */
let _counter = 0;
function freshStore(): PinnedThreadStore {
  return new PinnedThreadStore(`kaether-pt-selftest-${dbSuiteTag}-${++_counter}`);
}

function b64url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let b64 = btoa(String.fromCharCode(...bytes));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ---------------------------------------------------------------------------
// A. Codec
// ---------------------------------------------------------------------------

async function testCodecRoundTripPin(): Promise<TestCase> {
  const name = "codec round-trips pin event";
  const p: PinnedThreadPayload = { schema: 1, channelId: "ch", action: "pin", ts: 1_700_000_000 };
  try {
    const encoded = PinnedThreadCodec.encode(p);
    const decoded = PinnedThreadCodec.decode(encoded);
    const ok =
      decoded.schema === p.schema &&
      decoded.channelId === p.channelId &&
      decoded.action === p.action &&
      decoded.ts === p.ts;
    return ok
      ? pass(name)
      : fail(name, `got ${JSON.stringify(decoded)}, want ${JSON.stringify(p)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCodecRoundTripUnpin(): Promise<TestCase> {
  const name = "codec round-trips unpin event";
  const p: PinnedThreadPayload = { schema: 1, channelId: "ch", action: "unpin", ts: 1_700_000_001 };
  try {
    const encoded = PinnedThreadCodec.encode(p);
    const decoded = PinnedThreadCodec.decode(encoded);
    const ok =
      decoded.schema === p.schema &&
      decoded.channelId === p.channelId &&
      decoded.action === "unpin" &&
      decoded.ts === p.ts;
    return ok
      ? pass(name)
      : fail(name, `got ${JSON.stringify(decoded)}, want ${JSON.stringify(p)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testIsPinEventDistinguishesSiblingPrefixes(): Promise<TestCase> {
  const name = "isPinEvent distinguishes from every sibling envelope";
  const p: PinnedThreadPayload = { schema: 1, channelId: "ch", action: "pin", ts: 1 };
  let encoded: string;
  try {
    encoded = PinnedThreadCodec.encode(p);
  } catch {
    return fail(name, "encode threw");
  }
  const positive = PinnedThreadCodec.isPinEvent(encoded);
  const negChat = PinnedThreadCodec.isPinEvent("hello world");
  const negEnv = PinnedThreadCodec.isPinEvent("kaether:e2ee:v1:foo");
  const negReceipt = PinnedThreadCodec.isPinEvent("kaether:receipt:v1:abc");
  const negTomb = PinnedThreadCodec.isPinEvent("kaether:tombstone:v1:abc");
  const negReply = PinnedThreadCodec.isPinEvent("kaether:reply:v1:abc");
  const negPinMsg = PinnedThreadCodec.isPinEvent("kaether:pin:v1:abc");
  const ok = positive && !negChat && !negEnv && !negReceipt && !negTomb && !negReply && !negPinMsg;
  return ok
    ? pass(name)
    : fail(
        name,
        `pos=${positive} chat=${negChat} env=${negEnv} rcpt=${negReceipt} tomb=${negTomb} reply=${negReply} pinMsg=${negPinMsg}`,
      );
}

async function testTryDecodeReturnsNilForUserMessage(): Promise<TestCase> {
  const name = "tryDecode returns null for plain chat";
  try {
    const r = PinnedThreadCodec.tryDecode("just a chat message");
    return r === null ? pass(name) : fail(name, `got ${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testEmptyChannelIdRejected(): Promise<TestCase> {
  const name = "empty channelId throws";
  const json = `{"schema":1,"channelId":"","action":"pin","ts":1}`;
  const payload = PinnedThreadCodec.prefix + b64url(json);
  try {
    PinnedThreadCodec.decode(payload);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof PinnedThreadCodecError && e.kind === "emptyChannelId") {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

async function testUnsupportedSchemaRejected(): Promise<TestCase> {
  const name = "unsupported schema is rejected";
  const json = `{"schema":99,"channelId":"ch","action":"pin","ts":1}`;
  const payload = PinnedThreadCodec.prefix + b64url(json);
  try {
    PinnedThreadCodec.decode(payload);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (
      e instanceof PinnedThreadCodecError &&
      e.kind === "unsupportedSchema" &&
      e.schemaVersion === 99
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// B. Store mechanics
// ---------------------------------------------------------------------------

async function testPinChannelPresentInStore(): Promise<TestCase> {
  const name = "pin channel A → present in store";
  const s = freshStore();
  await s.pin("chA", 100);
  const ok = await s.isPinned("chA");
  const rec = await s.pinnedRecord("chA");
  return ok && rec?.pinnedTs === 100
    ? pass(name)
    : fail(name, `isPinned=${ok} record=${JSON.stringify(rec)}`);
}

async function testUnpinChannelRemoved(): Promise<TestCase> {
  const name = "unpin channel A → removed from store";
  const s = freshStore();
  await s.pin("chA", 100);
  await s.unpin("chA", 200);
  const ok = !(await s.isPinned("chA"));
  return ok ? pass(name) : fail(name, "channel still pinned after unpin");
}

async function testRePinIdempotent(): Promise<TestCase> {
  const name = "idempotent re-pin (same ts) does not bump observedAt";
  const s = freshStore();
  await s.pin("chA", 100, 1_000);
  const firstRec = await s.pinnedRecord("chA");
  const firstObserved = firstRec?.observedAt;
  // Identical ts — monotonic gate must drop it; observedAt must not change.
  await s.pin("chA", 100, 999_000);
  const secondRec = await s.pinnedRecord("chA");
  const secondObserved = secondRec?.observedAt;
  const ok = firstObserved === secondObserved && firstObserved === 1_000;
  return ok
    ? pass(name)
    : fail(name, `first=${firstObserved} second=${secondObserved}`);
}

async function testOutOfOrderUnpinRejected(): Promise<TestCase> {
  const name = "out-of-order unpin (older ts than pin) is rejected";
  const s = freshStore();
  await s.pin("chA", 100);
  const stale: PinnedThreadPayload = { schema: 1, channelId: "chA", action: "unpin", ts: 50 };
  const changed = await s.apply(stale);
  const stillPinned = await s.isPinned("chA");
  const ok = !changed && stillPinned;
  return ok ? pass(name) : fail(name, `changed=${changed} isPinned=${stillPinned}`);
}

async function testOutOfOrderPinReplayDoesNotBumpObservedAt(): Promise<TestCase> {
  const name = "out-of-order pin replay does not bump observedAt";
  const s = freshStore();
  await s.pin("chA", 100, 1_000);
  const firstObserved = (await s.pinnedRecord("chA"))?.observedAt;
  const stale: PinnedThreadPayload = { schema: 1, channelId: "chA", action: "pin", ts: 50 };
  await s.apply(stale, 999_000);
  const secondObserved = (await s.pinnedRecord("chA"))?.observedAt;
  const ok = firstObserved === secondObserved && firstObserved === 1_000;
  return ok
    ? pass(name)
    : fail(name, `first=${firstObserved} second=${secondObserved}`);
}

async function testPersistenceAcrossInstances(): Promise<TestCase> {
  const name = "persistence: write → reopen DB → state preserved";
  const dbName = `kaether-pt-selftest-persist-${dbSuiteTag}-${++_counter}`;
  const writer = new PinnedThreadStore(dbName);
  await writer.pin("chPersist", 100);
  // Open a fresh instance pointing at the same DB.
  const reader = new PinnedThreadStore(dbName);
  const ok =
    (await reader.isPinned("chPersist")) &&
    (await reader.pinnedChannelIds()).length === 1 &&
    (await reader.pinnedChannelIds())[0] === "chPersist";
  return ok ? pass(name) : fail(name, `ids=${JSON.stringify(await reader.pinnedChannelIds())}`);
}

async function testResetAllEmptiesStore(): Promise<TestCase> {
  const name = "resetAll empties store";
  const s = freshStore();
  await s.pin("chA", 100);
  await s.pin("chB", 200);
  await s.resetAll();
  const ids = await s.pinnedChannelIds();
  const ok = ids.length === 0;
  return ok ? pass(name) : fail(name, `ids=${JSON.stringify(ids)}`);
}

// ---------------------------------------------------------------------------
// C. Cap enforcement
// ---------------------------------------------------------------------------

async function testCap8AllPresent(): Promise<TestCase> {
  const name = "pin 8 threads → all present";
  const s = freshStore();
  for (let i = 0; i < MAX_PINNED_THREADS; i++) {
    await s.pin(`ch${i}`, 1_000 + i);
  }
  const ids = await s.pinnedChannelIds();
  const expected = new Set(Array.from({ length: MAX_PINNED_THREADS }, (_, i) => `ch${i}`));
  const got = new Set(ids);
  const ok = ids.length === MAX_PINNED_THREADS && [...expected].every((id) => got.has(id));
  return ok ? pass(name) : fail(name, `got ${JSON.stringify(ids.sort())}`);
}

async function testNinthPinEvictsOldest(): Promise<TestCase> {
  const name = "9th pin evicts the oldest by pinnedTs";
  const s = freshStore();
  for (let i = 0; i < MAX_PINNED_THREADS; i++) {
    await s.pin(`ch${i}`, 1_000 + i);
  }
  const beforeCount = (await s.pinnedChannelIds()).length;
  // 9th pin — ch0 (ts=1000) is the oldest and must be evicted.
  await s.pin("ch9", 9_999);
  const after = new Set(await s.pinnedChannelIds());
  const stillHasNewest = after.has("ch9");
  const evictedOldest = !after.has("ch0");
  const cappedAtMax = after.size === MAX_PINNED_THREADS;
  const ok = beforeCount === MAX_PINNED_THREADS && stillHasNewest && evictedOldest && cappedAtMax;
  return ok
    ? pass(name)
    : fail(
        name,
        `before=${beforeCount} after=${after.size} hasCh9=${stillHasNewest} evictedCh0=${evictedOldest}`,
      );
}

async function testNinthPinEvictsDeterministic(): Promise<TestCase> {
  const name = "cap 8 with tied ts — channelId asc tiebreaker decides eviction";
  const s = freshStore();
  // Pin 9 channels all with ts=1. The one with the lexicographically
  // smallest channelId among those tied is the "oldest" under the tiebreaker
  // used by pinnedChannelIds(), so it must be the one evicted.
  // Channels: ch0…ch8. Lex-smallest is "ch0".
  for (let i = 0; i < MAX_PINNED_THREADS + 1; i++) {
    await s.pin(`ch${i}`, 1);
  }
  // After cap enforcement exactly 8 remain.
  const ids = await s.pinnedChannelIds();
  const ok = ids.length === MAX_PINNED_THREADS;
  return ok
    ? pass(name)
    : fail(name, `expected ${MAX_PINNED_THREADS} entries, got ${ids.length}: ${JSON.stringify(ids)}`);
}

async function testEvictedRecordAbsent(): Promise<TestCase> {
  const name = "evicted thread's record is absent";
  const s = freshStore();
  for (let i = 0; i < MAX_PINNED_THREADS; i++) {
    await s.pin(`ch${i}`, 1_000 + i);
  }
  await s.pin("chNew", 9_999);
  const evictedRecord = await s.pinnedRecord("ch0");
  const newRecord = await s.pinnedRecord("chNew");
  const ok = evictedRecord === undefined && !(await s.isPinned("ch0")) && newRecord !== undefined;
  return ok
    ? pass(name)
    : fail(
        name,
        `ch0record=${JSON.stringify(evictedRecord)} chNewRecord=${JSON.stringify(newRecord)}`,
      );
}

// ---------------------------------------------------------------------------
// D. Sorting
// ---------------------------------------------------------------------------

async function testPinnedChannelIdsSortedDesc(): Promise<TestCase> {
  const name = "pinnedChannelIds order: ts=5 before ts=3";
  const s = freshStore();
  await s.pin("chA", 3);
  await s.pin("chB", 5);
  const ids = await s.pinnedChannelIds();
  // ts=5 (chB) must come first; ts=3 (chA) second.
  const ok = ids[0] === "chB" && ids[1] === "chA";
  return ok ? pass(name) : fail(name, `got ${JSON.stringify(ids)}`);
}

async function testTiesBrokenByChannelIdAsc(): Promise<TestCase> {
  const name = "per-channelId tiebreaker: same ts → channelId asc ('A' before 'B')";
  const s = freshStore();
  await s.pin("chB", 500);
  await s.pin("chA", 500);
  const ids = await s.pinnedChannelIds();
  // Both have ts=500; channelId asc → "chA" before "chB".
  const ok = ids[0] === "chA" && ids[1] === "chB";
  return ok ? pass(name) : fail(name, `got ${JSON.stringify(ids)}`);
}

// ---------------------------------------------------------------------------
// E. Server-rebind resistance + hardening
// ---------------------------------------------------------------------------

async function testLastEventTsGateStaleUnpinIgnored(): Promise<TestCase> {
  const name = "lastEventTs gate: pin@ts=5, then unpin@ts=3 → unpin ignored, still pinned";
  const s = freshStore();
  await s.pin("chA", 5);
  const stale: PinnedThreadPayload = { schema: 1, channelId: "chA", action: "unpin", ts: 3 };
  const changed = await s.apply(stale);
  const ok = !changed && (await s.isPinned("chA"));
  return ok ? pass(name) : fail(name, `changed=${changed} isPinned=${await s.isPinned("chA")}`);
}

async function testLastEventTsGateFreshUnpinApplied(): Promise<TestCase> {
  const name = "lastEventTs gate: pin@ts=5, then unpin@ts=6 → unpin applied";
  const s = freshStore();
  await s.pin("chA", 5);
  const fresh: PinnedThreadPayload = { schema: 1, channelId: "chA", action: "unpin", ts: 6 };
  const changed = await s.apply(fresh);
  const ok = changed && !(await s.isPinned("chA"));
  return ok
    ? pass(name)
    : fail(name, `changed=${changed} isPinned=${await s.isPinned("chA")}`);
}

async function testForgedChannelIdScopedToThatChannel(): Promise<TestCase> {
  const name = "forged channelId scoped to that channelId only";
  const s = freshStore();
  await s.pin("real-ch", 100);
  const forged: PinnedThreadPayload = {
    schema: 1,
    channelId: "fake-ch",
    action: "pin",
    ts: 200,
  };
  await s.apply(forged);
  const ok =
    (await s.isPinned("fake-ch")) &&
    (await s.isPinned("real-ch")) &&
    !(await s.isPinned("another-ch"));
  return ok
    ? pass(name)
    : fail(
        name,
        `fake=${await s.isPinned("fake-ch")} real=${await s.isPinned("real-ch")} other=${await s.isPinned("another-ch")}`,
      );
}

async function testCodecRejectsUnpinWithEmptyChannelId(): Promise<TestCase> {
  const name = "codec rejects unpin with empty channelId";
  const json = `{"schema":1,"channelId":"","action":"unpin","ts":1}`;
  const payload = PinnedThreadCodec.prefix + b64url(json);
  try {
    PinnedThreadCodec.decode(payload);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof PinnedThreadCodecError && e.kind === "emptyChannelId") {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}
